import React from 'react';
import { Check, Sparkles } from 'lucide-react';

const tiers = [
  {
    name: 'App Growth Review',
    price: 'From NOK 3,000',
    priceNote: 'or free for selected Norwegian app companies',
    duration: 'One-off',
    tagline: 'Teams that want an outside view before committing.',
    includes: [
      '30-minute intro call',
      'App-store and funnel review',
      'Acquisition opportunity scan',
      'Tracking / measurement check',
      'Short written recommendation',
    ],
    featured: false,
  },
  {
    name: 'Growth Sprint',
    price: 'From NOK 10,000',
    priceNote: '4–6 week engagement',
    duration: '4–6 weeks',
    tagline: 'Quick, focused progress on one growth bottleneck.',
    includes: [
      'Growth diagnosis',
      'Campaign or funnel setup',
      'Creative testing plan',
      'Weekly optimization',
      'Results review',
    ],
    featured: true,
  },
  {
    name: 'Performance Growth Retainer',
    price: 'From NOK 15,000/mo',
    priceNote: 'Ongoing partnership',
    duration: 'Monthly',
    tagline: 'Apps actively investing in paid growth and recurring optimization.',
    includes: [
      'Channel strategy',
      'Campaign management',
      'Creative testing',
      'Reporting',
      'CRO / ASO recommendations',
      'Monthly growth roadmap',
    ],
    featured: false,
  },
  {
    name: 'Scaling Partnership',
    price: 'Custom',
    priceNote: 'Typical engagement',
    duration: 'Long-term',
    tagline: 'Established app companies entering new markets or scaling budgets.',
    includes: [
      'Multi-channel strategy',
      'Market expansion',
      'Analytics & attribution support',
      'Creative testing system',
      'Senior growth advisory',
      'Stakeholder reporting',
    ],
    featured: false,
  },
];

const Engagement = () => {
  const scrollToForm = () =>
    document.getElementById('app-growth-lead-form')?.scrollIntoView({ behavior: 'smooth' });

  return (
    <section
      data-testid="app-growth-engagement"
      className="py-24 px-4 sm:px-6 lg:px-8 bg-gray-50"
    >
      <div className="max-w-7xl mx-auto">
        <div className="max-w-3xl mb-14">
          <div className="inline-flex items-center gap-2 text-blue-600 text-sm font-semibold uppercase tracking-wider mb-4">
            Pricing & engagement
          </div>
          <h2 className="text-3xl sm:text-4xl lg:text-5xl font-bold text-gray-900 leading-tight tracking-tight">
            Simple ways to start.
          </h2>
          <p className="mt-6 text-lg text-gray-600 leading-relaxed">
            Every app has a different growth model, but buyers should not need a long sales process
            just to understand how we work. Start with a focused review, then move into a sprint or
            ongoing growth partnership.
          </p>
        </div>

        <div className="grid md:grid-cols-2 lg:grid-cols-4 gap-6">
          {tiers.map((t, i) => (
            <div
              key={i}
              data-testid={`engagement-tier-${i}`}
              className={`relative rounded-2xl p-7 flex flex-col ${
                t.featured
                  ? 'bg-blue-600 text-white border-2 border-blue-400 shadow-2xl lg:-translate-y-2'
                  : 'bg-white border border-gray-100 hover:shadow-lg transition-shadow'
              }`}
            >
              {t.featured && (
                <div className="absolute -top-3 left-1/2 -translate-x-1/2 inline-flex items-center gap-1 px-3 py-1 rounded-full bg-blue-300 text-gray-900 text-xs font-bold uppercase tracking-wide">
                  <Sparkles size={12} /> Most popular
                </div>
              )}
              <div
                className={`text-xs uppercase tracking-wide font-semibold mb-2 ${
                  t.featured ? 'text-blue-300' : 'text-blue-600'
                }`}
              >
                {t.duration}
              </div>
              <h3
                className={`text-xl font-bold mb-2 ${
                  t.featured ? 'text-white' : 'text-gray-900'
                }`}
              >
                {t.name}
              </h3>
              <p
                className={`text-sm leading-relaxed mb-5 ${
                  t.featured ? 'text-blue-100/80' : 'text-gray-600'
                }`}
              >
                {t.tagline}
              </p>
              <div
                className={`text-2xl font-bold mb-1 ${
                  t.featured ? 'text-white' : 'text-gray-900'
                }`}
              >
                {t.price}
              </div>
              <div
                className={`text-xs mb-6 ${
                  t.featured ? 'text-blue-100/60' : 'text-gray-500'
                }`}
              >
                {t.priceNote}
              </div>
              <ul className="space-y-2.5 flex-1 mb-6">
                {t.includes.map((it, j) => (
                  <li
                    key={j}
                    className={`text-sm flex items-start gap-2 ${
                      t.featured ? 'text-blue-50' : 'text-gray-700'
                    }`}
                  >
                    <Check
                      size={14}
                      className={`flex-shrink-0 mt-0.5 ${
                        t.featured ? 'text-blue-300' : 'text-blue-600'
                      }`}
                      strokeWidth={3}
                    />
                    {it}
                  </li>
                ))}
              </ul>
              <button
                onClick={scrollToForm}
                className={`w-full py-3 rounded-lg font-semibold text-sm transition-colors ${
                  t.featured
                    ? 'bg-blue-300 hover:bg-blue-200 text-gray-900'
                    : 'bg-blue-600 hover:bg-blue-700 text-white'
                }`}
              >
                Get started
              </button>
            </div>
          ))}
        </div>
      </div>
    </section>
  );
};

export default Engagement;
